import { useDataSource } from 'hooks/dataSource/useDataSource'
import {
  useLazyGetTasksQuery,
  useUpdateTaskDurationMutation,
} from '@crew/apis/task/taskApis'
import { TaskDuration } from '@crew/apis/task/models/updateTaskDuration/request'
import dayjs from '@crew/modules/dayjs'
import { JsonDateFormat } from '@crew/enums/system'
import { cloneDeep } from 'lodash'
import { RefObject } from 'react'
import { Scheduler } from 'devextreme-react'
import { GetTasksRequest } from '@crew/apis/task/models/getTasks/request'

//  DataSourceのloadOptions.filterがany型のため、
//  パラメータがSearchTasksParam型かをチェックする型ガード関数
const isSearchMeTasksParam = (filter: any): filter is SearchTasksParam => {
  if (typeof filter !== 'object' || filter === null || Array.isArray(filter)) {
    return false
  }

  // 各プロパティの型チェック
  if (
    'keyword' in filter &&
    typeof filter.keyword !== 'string' &&
    filter.keyword !== undefined
  ) {
    return false
  }
  if (
    'taskKindIds' in filter &&
    !Array.isArray(filter.taskKindIds) &&
    filter.taskKindIds !== undefined
  ) {
    return false
  }
  if (
    'assignToUser' in filter &&
    typeof filter.assignToUser !== 'string' &&
    filter.assignToUser !== undefined
  ) {
    return false
  }
  if (
    'taskStateIds' in filter &&
    !Array.isArray(filter.taskStateIds) &&
    filter.taskStateIds !== undefined
  ) {
    return false
  }
  if (
    'taskStateTypes' in filter &&
    !Array.isArray(filter.taskStateTypes) &&
    filter.taskStateTypes !== undefined
  ) {
    return false
  }
  if (
    'taskPriorities' in filter &&
    !Array.isArray(filter.taskPriorities) &&
    filter.taskPriorities !== undefined
  ) {
    return false
  }
  if (
    'taskCategoryIds' in filter &&
    !Array.isArray(filter.taskCategoryIds) &&
    filter.taskCategoryIds !== undefined
  ) {
    return false
  }
  if (
    'projectId' in filter &&
    typeof filter.projectId !== 'string' &&
    filter.projectId !== undefined
  ) {
    return false
  }
  if (
    'projectGroupIds' in filter &&
    !Array.isArray(filter.projectGroupIds) &&
    filter.projectGroupIds !== undefined
  ) {
    return false
  }
  if (
    'startDate' in filter &&
    typeof filter.startDate !== 'string' &&
    filter.startDate !== undefined
  ) {
    return false
  }
  if (
    'dueDate' in filter &&
    typeof filter.dueDate !== 'string' &&
    filter.dueDate !== undefined
  ) {
    return false
  }
  if (
    'createdById' in filter &&
    typeof filter.createdById !== 'string' &&
    filter.createdById !== undefined
  ) {
    return false
  }
  if (
    'updatedById' in filter &&
    typeof filter.updatedById !== 'string' &&
    filter.updatedById !== undefined
  ) {
    return false
  }
  if (
    'createdAt' in filter &&
    typeof filter.createdAt !== 'string' &&
    filter.createdAt !== undefined
  ) {
    return false
  }
  if (
    'updatedAt' in filter &&
    typeof filter.updatedAt !== 'string' &&
    filter.updatedAt !== undefined
  ) {
    return false
  }

  return true
}

export type SearchTasksParam = {
  // キーワード
  keyword?: string
  // タスク種別ID
  taskKindIds?: string[]
  // 担当者ID
  assignToUser?: string | undefined
  // ステータスID
  taskStateIds?: string[]
  // ステータス種別
  taskStateTypes?: string[]
  // 優先度
  taskPriorities?: number[]
  // タスク分類ID
  taskCategoryIds?: string[]
  // プロジェクトID
  projectId?: string
  // プロジェクトグループID
  projectGroupIds?: string[]
  // 開始日
  startDate?: string
  // 期日
  dueDate?: string
  // 登録者
  createdById?: string
  // 更新者
  updatedById?: string
  // 登録日
  createdAt?: string
  // 更新日
  updatedAt?: string
}

export const useTaskCalendar = (schedulerRef: RefObject<Scheduler>) => {
  // タスクの期間更新API
  const [updateTaskDurationMutation] = useUpdateTaskDurationMutation()
  // タスク一覧取得API
  const [lazyGetTasksQuery] = useLazyGetTasksQuery()

  // タスク一覧DataSource
  const tasksDataSource = useDataSource(
    () => ({
      key: 'id',
      load: async (loadOptions) => {
        if (!schedulerRef.current) return []

        // パラメータの型が一致していない場合は空配列を返却
        if (!(loadOptions.filter && isSearchMeTasksParam(loadOptions.filter))) {
          return []
        }
        const filterParams = loadOptions.filter as SearchTasksParam

        // カレンダーの表示期間（開始日、末日）を取得
        const targetDateFrom = schedulerRef.current.instance.getStartViewDate()
        const targetDateTo = schedulerRef.current.instance.getEndViewDate()

        // タスク一覧取得APIのパラメータを設定
        const params: GetTasksRequest = {
          keyword: filterParams.keyword,
          taskKindIds: filterParams.taskKindIds,
          assignToUser: filterParams.assignToUser,
          taskStateIds: filterParams.taskStateIds,
          taskPriorities: filterParams.taskPriorities,
          taskCategoryIds: filterParams.taskCategoryIds,
          projectId: filterParams.projectId,
          projectGroupIds: filterParams.projectGroupIds,
          startDate: filterParams.startDate,
          dueDate: filterParams.dueDate,
          createdById: filterParams.createdById,
          updatedById: filterParams.updatedById,
          createdAt: filterParams.createdAt,
          updatedAt: filterParams.updatedAt,
          taskStateTypes: filterParams.taskStateTypes,
          // カレンダーの表示対象期間のみ取得
          targetDateFrom: dayjs(targetDateFrom).format(JsonDateFormat.YYYYMMDD),
          targetDateTo: dayjs(targetDateTo).format(JsonDateFormat.YYYYMMDD),
          // この画面で使用しないパラメータはundefinedを設定
          keywordFilterCondition: undefined,
          parentTaskId: undefined,
          limit: undefined,
          offset: undefined,
          sort: undefined,
        }

        // タスク一覧を取得
        const response = await lazyGetTasksQuery(params).unwrap()

        // response.tasksは読み取り専用のオブジェクトのため、カレンダー上にタスクをドラグしたときresponse.tasks内の項目を直接編集することができない。
        // そのため、別のオブジェクトにresponse.tasksをクローンし編集できるようにする。
        const tasksClone = cloneDeep(response.tasks)

        // カレンダー表示用にタスクの開始日・期日を調整
        const tasks = tasksClone.map((task) => {
          const tempTask = Object.assign({}, task)

          // 開始日が未設定・期日が設定済みの場合
          // Figmaに書いてあるとおり、期日指定されている一日で表示する
          if (!task.startDate && task.dueDate) {
            return {
              ...tempTask,
              startDate: task.dueDate,
            }
          }

          // 開始日が設定済み・期日が未設定の場合
          // 表示しない
          if (task.startDate && !task.dueDate) {
            return {
              ...tempTask,
              startDate: null,
            }
          }

          return {
            ...tempTask,
          }
        })

        return tasks
      },
      update: async (key, values) => {
        const payload: TaskDuration = {
          taskId: key,
          startDate: values.startDate
            ? dayjs(values.startDate).format(JsonDateFormat.YYYYMMDD)
            : undefined,
          dueDate: values.dueDate
            ? dayjs(values.dueDate).format(JsonDateFormat.YYYYMMDD)
            : undefined,
          assignToUserId: values.assignToUser
            ? values.assignToUser.id
            : undefined,
          version: values.version,
        }

        // タスク期間更新APIを実行
        await updateTaskDurationMutation({
          task: {
            ...payload,
          },
        }).unwrap()
      },
    }),
    [lazyGetTasksQuery, schedulerRef, updateTaskDurationMutation]
  )

  return {
    tasksDataSource,
  }
}
